<template>
    <section>
    <div class="guest-checkout-container payment" v-show="isOpen">
        <div class="flex flex-row" style="justify-content: space-between;">
            <p class="title-l text-rothcoBlack">Payment</p>
            <div v-if="props.user?.email" class="bg-rothcoCream accordion-button" @click="isOpen = false">
                <AccordionArrow class="icon-open" />
            </div>
        </div>
        <p class="paragraph-details transactions-message">All transactions are secured and encrypted.</p>
        <div class="radio-group" role="radiogroup" :aria-required="true">
            <div class="option" @click="selectedPaymentMethod = 'card'" v-if="!isLoggedIn || !props.creditCards.length">
                <div class="card_option">
                    <label class="card-wrapper">
                        <input type="radio" name="payment_method" id="payment_method_1" value="card" checked>
                        <div class="label-container">
                            <span class="paragraph-details semibold card-label" for="payment_method_1">Credit or debit card</span>
                        </div>
                        <div class="logos-container">
                            <VisaIcon alt="visa-logo"/>
                            <MasterCardIcon alt="mastercard-logo"/>
                            <AmexIcon alt="amex-logo"/>
                            <PaypalIcon alt="paypal-logo"/>
                            <DiscoverIcon alt="discover-logo"/>
                        </div>
                    </label>
                    <div class="card-form-fields toggle-on-check" v-if="selectedPaymentMethod == 'card'">
                        <div class="authorization-input_label">
                            <InputMask 
                                :aria-required="selectedPaymentMethod == 'card'"
                                @focus="togglePlaceholder('card-number')"
                                @blur="togglePlaceholder('card-number')"
                                :autoClear="false"
                                id="card-number" v-model="cardNumber" mask="9999 9999 9999 9?999 999" :class="{
                                    'authorization-card-number_input': true,
                                    'invalid-state-input': errors.card_number != null
                                }"
                            :placeholder="!placeholdersHidden.get('card-number') ? 'Card Number *' : ''" 
                            fluid />
                            <label
                            :class="{'invalid-state': errors.card_number}"
                            for="card-number" class="field-with-icon"><p>Card Number *</p><LockIcon alt="lock-icon"/></label>
                            <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.card_number">{{ errors.card_number }}</p></div>
                        </div>
                        <div class="grid-2">
                            <div class="authorization-input_label">
                                <InputMask 
                                    :aria-required="selectedPaymentMethod == 'card'"
                                    @focus="togglePlaceholder('expiration-date')"
                                    @blur="togglePlaceholder('expiration-date')"
                                    id="expiration-date" v-model="expirationDate" mask="99/99" :class="{
                                        'authorization-expiration-date_input': true,
                                        'invalid-state-input': errors.card_expiration != null
                                    }"
                                :placeholder="!placeholdersHidden.get('expiration-date') ? 'Expiration Date (MM/YY) *' : ''" 
                                fluid />
                                <label
                                :class="{'invalid-state': errors.card_expiration}"
                                for="expiration-date"><p>Expiration Date (MM/YY) *</p></label>
                                <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.card_expiration">{{ errors.card_expiration }}</p></div>
                            </div>
                            <div class="authorization-input_label">
                                <InputMask 
                                    :aria-required="selectedPaymentMethod == 'card'"
                                    @focus="togglePlaceholder('security-code')"
                                    @blur="togglePlaceholder('security-code')"
                                    id="security-code" v-model="securityCode" mask="999?9" :class="{
                                        'authorization-security-code_input': true,
                                        'invalid-state-input': errors.card_cvv != null
                                    }"
                                :placeholder="!placeholdersHidden.get('security-code') ? 'Security Code *' : ''" 
                                fluid />
                                <label
                                :class="{'invalid-state': errors.card_cvv}"
                                for="security-code" class="field-with-icon"><p>Security Code *</p>
                                <!--<img src="@icons/support-icon.svg" alt="question mark icon"/>-->
                                </label>
                                <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.card_cvv">{{ errors.card_cvv }}</p></div>
                            </div>
                        </div>
                        <div class="authorization-input_label">
                            <InputText 
                                :aria-required="selectedPaymentMethod == 'card'"
                                @focus="togglePlaceholder('name-on-card')"
                                @blur="togglePlaceholder('name-on-card')"
                                id="name-on-card" v-model="nameOnCard"
                                :class="{
                                    'authorization-name-on-card_input': true,
                                    'invalid-state-input': errors.card_name != null,
                                }"
                                :placeholder="!placeholdersHidden.get('name-on-card') ? 'Name On Card *' : ''" 
                                fluid
                            />
                            <label
                                :class="{'invalid-state': errors.card_name}"
                                for="name-on-card">
                                <p>Name On Card *</p>
                            </label>
                            <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.card_name">{{ errors.card_name }}</p></div>
                        </div>
                        <div class="checkbox-container">
                            <label class="container">
                                <input type="checkbox" v-model="useShippingAsBilling" id="use_billing" checked/>
                                <p class="paragraph-details">Use shipping address as billing address</p>
                            </label>
                        </div>
                        <div class="grid-2 hide-billing">
                            <div class="authorization-input_label">
                                <InputText 
                                    :aria-required="selectedPaymentMethod == 'card' && !useShippingAsBilling"
                                    @focus="togglePlaceholder('billing_first_name')"
                                    @blur="togglePlaceholder('billing_first_name')"
                                    id="billing_first_name" v-model="billingFirstName"
                                    :class="{
                                        'authorization-name-on-card_input': true,
                                        'invalid-state-input': errors.billing_first_name != null,
                                    }"
                                    :placeholder="!placeholdersHidden.get('billing_first_name') ? 'First Name / Company *' : ''" 
                                    fluid
                                />
                                <label
                                    :class="{'invalid-state': errors.billing_first_name}"
                                    for="billing_first_name">
                                    <p>First Name / Company *</p>
                                </label>
                                <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.billing_first_name">{{ errors.billing_first_name }}</p></div>
                            </div>
                            <div class="authorization-input_label">
                                <InputText 
                                    @focus="togglePlaceholder('billing_last_name')"
                                    @blur="togglePlaceholder('billing_last_name')"
                                    id="billing_last_name" v-model="billingLastName"
                                    :class="{
                                        'authorization-name-on-card_input': true,
                                        'invalid-state-input': errors.billing_last_name != null,
                                    }"
                                    :placeholder="!placeholdersHidden.get('billing_last_name') ? 'Last Name' : ''" 
                                    fluid
                                />
                                <label
                                    :class="{'invalid-state': errors.billing_last_name}"
                                    for="billing_last_name">
                                    <p>Last Name</p>
                                </label>
                                <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.billing_last_name">{{ errors.billing_last_name }}</p></div>
                            </div>
                        </div>
                        <div class="authorization-input_label hide-billing flex-column">
                            <InputText 
                                :aria-required="selectedPaymentMethod == 'card' && !useShippingAsBilling"
                                @focus="togglePlaceholder('billing_address')"
                                @blur="togglePlaceholder('billing_address')"
                                id="billing_address" v-model="billingAddress"
                                :class="{
                                    'authorization-name-on-card_input': true,
                                    'invalid-state-input': errors.billing_address != null,
                                }"
                                :placeholder="!placeholdersHidden.get('billing_address') ? 'Address *' : ''" 
                                fluid
                            />
                            <label
                                :class="{'invalid-state': errors.billing_address}"
                                for="billing_address">
                                <p>Address *</p>
                            </label>
                            <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.billing_address">{{ errors.billing_address }}</p></div>
                        </div>
                        <p v-if="addSecondAddress == false" class="add-apartment paragraph-details text-rothcoRed hide-billing" @click="addSecondAddress = true">+ Add apartment, suite, etc.</p>
                        <div v-if="addSecondAddress" class="authorization-input_label hide-billing">
                            <InputText 
                                @focus="togglePlaceholder('billing_address_2')"
                                @blur="togglePlaceholder('billing_address_2')"
                                id="billing_address_2" v-model="billingAddress2"
                                :class="{
                                    'authorization-name-on-card_input': true,
                                }"
                                :placeholder="!placeholdersHidden.get('billing_address_2') ? 'Address Line 2 (Optional)' : ''" 
                                fluid
                            />
                            <label
                                for="billing_address_2">
                                <p>Address Line 2 (Optional)</p>
                            </label>
                        </div>
                        <div class="authorization-input_label hide-billing flex-column">
                            <InputText 
                                :aria-required="selectedPaymentMethod == 'card' && !useShippingAsBilling"
                                @focus="togglePlaceholder('billing_city')"
                                @blur="togglePlaceholder('billing_city')"
                                id="billing_city" v-model="billingCity"
                                :class="{
                                    'authorization-name-on-card_input': true,
                                    'invalid-state-input': errors.billing_city != null,
                                }"
                                :placeholder="!placeholdersHidden.get('billing_city') ? 'City *' : ''" 
                                fluid
                            />
                            <label
                                :class="{'invalid-state': errors.billing_city}"
                                for="billing_city">
                                <p>City *</p>
                            </label>
                            <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.billing_city">{{ errors.billing_city }}</p></div>
                        </div>
                        <div class="grid-2 hide-billing">
                            <div class="selectore-state select-wrapper">
                                <CustomSelect
                                    :aria-required="selectedPaymentMethod == 'card' && !useShippingAsBilling"
                                    name="billing_state" id="billing_state" label="State" v-model="selectedBillingState" :options="props.regions"
                                    :class="{'invalid-state-input': errors.billing_state != null}"
                                />
                                <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.billing_state">{{ errors.billing_state }}</p></div>
                            </div>
                            <div class="authorization-input_label">
                                <InputText 
                                    :aria-required="selectedPaymentMethod == 'card' && !useShippingAsBilling"
                                    @focus="togglePlaceholder('billing_zipcode')"
                                    @blur="togglePlaceholder('billing_zipcode')"
                                    id="billing_zipcode" v-model="billingZip"
                                    :class="{
                                        'authorization-name-on-card_input': true,
                                        'invalid-state-input': errors.billing_zip != null,
                                    }"
                                    :placeholder="!placeholdersHidden.get('billing_zipcode') ? 'Zipcode *' : ''" 
                                    fluid
                                />
                                <label
                                    :class="{'invalid-state': errors.billing_zip}"
                                    for="billing_zipcode">
                                    <p>Zipcode *</p>
                                </label>
                                <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.billing_zip">{{ errors.billing_zip }}</p></div>
                            </div>
                        </div>
                        <div class="checkbox-container" v-if="isLoggedIn">
                            <label class="container">
                                <input type="checkbox" v-model="saveCard" id="save_card"/>
                                <p class="paragraph-details">Save this card for future use</p>
                            </label>
                        </div>
                        <div class="checkbox-container" v-if="isLoggedIn">
                            <label class="container">
                                <input type="checkbox" v-model="setAsDefault" id="set_as_default"/>
                                <p class="paragraph-details">Set this card as default</p>
                            </label>
                        </div>
                        <p class="paragraph-details block hide-billing" v-if="!isLoggedIn">
                            Rothco currently only ships within the United States. If you live outside of the US please search for a dealer in your country <a :href="route('retail.locator.show')" class="paragraph-details">here.</a>
                        </p>
                    </div>
                </div>
            </div>
            <label v-for="(card, index) in creditCardsSorted" :key="index" 
                class="option" :id="`payment_method_cc_container_${card.consumer_credit_card_id}`"
                :style="index == 0 ? `border-radius: 0.313rem 0.313rem 0 0; padding: .75rem 1.25rem; ${isCardExpired(card.cc_exp) ? `cursor: not-allowed;` : ``}` : `${isCardExpired(card.cc_exp) ? `cursor: not-allowed;` : ``}`">
                <input :disabled="isCardExpired(card.cc_exp)" type="radio" name="payment_method" 
                    :id="`payment_method_cc_input_${card.consumer_credit_card_id}`" 
                    v-model="selectedPaymentMethod" :value="card.consumer_credit_card_id"
                    :checked="selectedPaymentMethod == card.consumer_credit_card_id"
                    @click="emits('creditCardUpdated')"
                >
                <div class="label-container">
                    <div class="image-container">
                        <MasterCardIcon v-if="card.cc_type == 'Master Card' || card.cc_type == 'MasterCard'" alt="mastercard-logo"/>
                        <VisaIcon v-if="card.cc_type == 'Visa'" alt="visa-logo"/>
                        <AmexIcon v-if="card.cc_type == 'American Express'" alt="amex-logo"/>
                        <DisoverIcon v-if="card.cc_type == 'Discover'" alt="discover-logo"/>
                        <span class="paragraph-details semibold" for="payment_method_select">{{ card.cc_type }} &bull;&bull;&bull;&bull; {{ card.cc_last_4 }}</span>
                        <span v-if="isCardExpired(card.cc_exp)" style="font-weight: bold;" class="text-rothcoRed">Expired</span>
                        <WarningIcon v-if="isCardExpired(card.cc_exp)"/>

                    </div>
                    <p class="paragraph-details estimation" v-if="card.cc_street">{{card.cc_name}}, {{ card.cc_street }}, {{ card.cc_city }}, {{ card.cc_state }} {{ card.cc_zip }}, {{ card.cc_country }}</p>
                </div>
            </label>
            <!-- STRIPE EXPRESS CHECKOUT -->
            <label v-if="isStripeExpressCheckoutAvailable" class="option" :style="!openNewCard || !isLoggedIn ? `border-radius: 0 0 0 0;` : ``" @click="selectedPaymentMethod = 'stripe_express_checkout'">
                <input type="radio" name="payment_method" id="payment_method_2" value="stripe_express_checkout" :checked="selectedPaymentMethod == 'stripe_express_checkout'">
                <div class="label-container">
                    <div class="image-container">
                        <StripeExpressCheckoutButton
                            ref="stripeExpressCheckoutButtonRef"
                            :stripe-publishable-key="stripePublishableKey" 
                            :amount="totalAmount"
                            currency="usd"
                            :country="'US'"
                            :disabled="!canProcessStripeExpressCheckout"
                            v-model:is-express-checkout-available="isStripeExpressCheckoutAvailable"
                            @payment-success="handleStripeExpressCheckoutSuccess"
                        />
                    </div>
                </div>
            </label>
            
            <!-- PAYPAL -->
            <label class="option" :style="!openNewCard || !isLoggedIn ? `border-radius: 0 0 0.313rem 0.313rem;` : ``" @click="selectedPaymentMethod = 'paypal'">
                <input type="radio" name="payment_method" id="payment_method_3" value="paypal" :checked="selectedPaymentMethod == 'paypal'">
                <div class="label-container">
                    <div class="image-container">
                        <PaypalIcon alt="paypal-logo"/>
                        <span class="paragraph-details semibold" for="payment_method_3">PayPal</span>
                    </div>
                    <div v-show="selectedPaymentMethod == 'paypal'" style="padding-top:10px;" id="paypal-button-container"></div>
                    <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.paypal_order_id && paypalAttempted">{{ errors.paypal_order_id }}</p></div>
                </div>
            </label>
            <p v-if="!openNewCard && isLoggedIn && props.creditCards.length" 
                class="add-apartment paragraph-details text-rothcoRed hide-billing" 
                style="cursor: pointer;"
                @click="setCardPaymentMethod">
                + Use a different credit card.
            </p>
            <label class="option" style="padding: 0;" v-if="openNewCard && isLoggedIn && props.creditCards.length">
                <div class="card_option">
                    <label class="card-wrapper">
                        <input type="radio" name="payment_method" id="payment_method_1" value="card" :checked="selectedPaymentMethod == 'card'" @click="selectedPaymentMethod = 'card'">
                        <div class="label-container">
                            <span class="paragraph-details semibold card-label" for="payment_method_1">New credit or debit card</span>
                        </div>
                        <div class="logos-container">
                            <VisaIcon alt="visa-logo"/>
                            <MasterCardIcon alt="mastercard-logo"/>
                            <AmexIcon alt="amex-logo"/>
                            <DiscoverIcon alt="discover-logo"/>
                        </div>
                    </label>
                    <div class="card-form-fields toggle-on-check" v-if="selectedPaymentMethod == 'card'">
                        <div class="authorization-input_label">
                            <InputMask 
                                :aria-required="selectedPaymentMethod == 'card'"
                                @focus="togglePlaceholder('card-number')"
                                @blur="togglePlaceholder('card-number')"
                                :autoClear="false"
                                id="card-number" v-model="cardNumber" mask="9999 9999 9999 9?999 999" :class="{
                                    'authorization-card-number_input': true,
                                    'invalid-state-input': errors.card_number != null
                                }"
                            :placeholder="!placeholdersHidden.get('card-number') ? 'Card Number *' : ''" 
                            fluid />
                            <label
                            :class="{'invalid-state': errors.card_number}"
                            for="card-number" class="field-with-icon"><p>Card Number *</p><LockIcon alt="lock-icon"/></label>
                            <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.card_number">{{ errors.card_number }}</p></div>
                        </div>
                        <div class="grid-2">
                            <div class="authorization-input_label">
                                <InputMask 
                                    :aria-required="selectedPaymentMethod == 'card'"
                                    @focus="togglePlaceholder('expiration-date')"
                                    @blur="togglePlaceholder('expiration-date')"
                                    id="expiration-date" v-model="expirationDate" mask="99/99" :class="{
                                        'authorization-expiration-date_input': true,
                                        'invalid-state-input': errors.card_expiration != null
                                    }"
                                :placeholder="!placeholdersHidden.get('expiration-date') ? 'Expiration Date (MM/YY) *' : ''" 
                                fluid />
                                <label
                                :class="{'invalid-state': errors.card_expiration}"
                                for="expiration-date"><p>Expiration Date (MM/YY) *</p></label>
                                <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.card_expiration">{{ errors.card_expiration }}</p></div>
                            </div>
                            <div class="authorization-input_label">
                                <InputMask 
                                    :aria-required="selectedPaymentMethod == 'card'"
                                    @focus="togglePlaceholder('security-code')"
                                    @blur="togglePlaceholder('security-code')"
                                    id="security-code" v-model="securityCode" mask="999?9" :class="{
                                        'authorization-security-code_input': true,
                                        'invalid-state-input': errors.card_cvv != null
                                    }"
                                :placeholder="!placeholdersHidden.get('security-code') ? 'Security Code *' : ''" 
                                fluid />
                                <label
                                :class="{'invalid-state': errors.card_cvv}"
                                for="security-code" class="field-with-icon"><p>Security Code *</p>
                                <!-- <img src="@icons/support-icon.svg" alt="question mark icon"/>-->
                                </label>
                                <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.card_cvv">{{ errors.card_cvv }}</p></div>
                            </div>
                        </div>
                        <div class="authorization-input_label">
                            <InputText 
                                :aria-required="selectedPaymentMethod == 'card'"
                                @focus="togglePlaceholder('name-on-card')"
                                @blur="togglePlaceholder('name-on-card')"
                                id="name-on-card" v-model="nameOnCard"
                                :class="{
                                    'authorization-name-on-card_input': true,
                                    'invalid-state-input': errors.card_name != null,
                                }"
                                :placeholder="!placeholdersHidden.get('name-on-card') ? 'Name On Card *' : ''" 
                                fluid
                            />
                            <label
                                :class="{'invalid-state': errors.card_name}"
                                for="name-on-card">
                                <p>Name On Card *</p>
                            </label>
                            <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.card_name">{{ errors.card_name }}</p></div>
                        </div>
                        <div class="checkbox-container">
                            <label class="container">
                                <input type="checkbox" v-model="useShippingAsBilling" id="use_billing" checked/>
                                <p class="paragraph-details">Use shipping address as billing address</p>
                            </label>
                        </div>
                        <div class="grid-2 hide-billing">
                            <div class="authorization-input_label">
                                <InputText 
                                    :aria-required="selectedPaymentMethod == 'card' && !useShippingAsBilling"
                                    @focus="togglePlaceholder('billing_first_name')"
                                    @blur="togglePlaceholder('billing_first_name')"
                                    id="billing_first_name" v-model="billingFirstName"
                                    :class="{
                                        'authorization-name-on-card_input': true,
                                        'invalid-state-input': errors.billing_first_name != null,
                                    }"
                                    :placeholder="!placeholdersHidden.get('billing_first_name') ? 'First Name / Company *' : ''" 
                                    fluid
                                />
                                <label
                                    :class="{'invalid-state': errors.billing_first_name}"
                                    for="billing_first_name">
                                    <p>First Name / Company *</p>
                                </label>
                                <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.billing_first_name">{{ errors.billing_first_name }}</p></div>
                            </div>
                            <div class="authorization-input_label">
                                <InputText 
                                    @focus="togglePlaceholder('billing_last_name')"
                                    @blur="togglePlaceholder('billing_last_name')"
                                    id="billing_last_name" v-model="billingLastName"
                                    :class="{
                                        'authorization-name-on-card_input': true,
                                        'invalid-state-input': errors.billing_last_name != null,
                                    }"
                                    :placeholder="!placeholdersHidden.get('billing_last_name') ? 'Last Name' : ''" 
                                    fluid
                                />
                                <label
                                    :class="{'invalid-state': errors.billing_last_name}"
                                    for="billing_last_name">
                                    <p>Last Name</p>
                                </label>
                                <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.billing_last_name">{{ errors.billing_last_name }}</p></div>
                            </div>
                        </div>
                        <div class="authorization-input_label hide-billing flex-column">
                            <InputText 
                                :aria-required="selectedPaymentMethod == 'card' && !useShippingAsBilling"
                                @focus="togglePlaceholder('billing_address')"
                                @blur="togglePlaceholder('billing_address')"
                                id="billing_address" v-model="billingAddress"
                                :class="{
                                    'authorization-name-on-card_input': true,
                                    'invalid-state-input': errors.billing_address != null,
                                }"
                                :placeholder="!placeholdersHidden.get('billing_address') ? 'Address *' : ''" 
                                fluid
                            />
                            <label
                                :class="{'invalid-state': errors.billing_address}"
                                for="billing_address">
                                <p>Address *</p>
                            </label>
                            <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.billing_address">{{ errors.billing_address }}</p></div>
                        </div>
                        <p v-if="addSecondAddress == false" class="add-apartment paragraph-details text-rothcoRed hide-billing" @click="addSecondAddress = true">+ Add apartment, suite, etc.</p>
                        <div v-if="addSecondAddress" class="authorization-input_label hide-billing">
                            <InputText 
                                @focus="togglePlaceholder('billing_address_2')"
                                @blur="togglePlaceholder('billing_address_2')"
                                id="billing_address_2" v-model="billingAddress2"
                                :class="{
                                    'authorization-name-on-card_input': true,
                                }"
                                :placeholder="!placeholdersHidden.get('billing_address_2') ? 'Address Line 2 (Optional)' : ''" 
                                fluid
                            />
                            <label
                                for="billing_address_2">
                                <p>Address Line 2 (Optional)</p>
                            </label>
                        </div>
                        <div class="authorization-input_label hide-billing flex-column">
                            <InputText 
                                :aria-required="selectedPaymentMethod == 'card' && !useShippingAsBilling"
                                @focus="togglePlaceholder('billing_city')"
                                @blur="togglePlaceholder('billing_city')"
                                id="billing_city" v-model="billingCity"
                                :class="{
                                    'authorization-name-on-card_input': true,
                                    'invalid-state-input': errors.billing_city != null,
                                }"
                                :placeholder="!placeholdersHidden.get('billing_city') ? 'City *' : ''" 
                                fluid
                            />
                            <label
                                :class="{'invalid-state': errors.billing_city}"
                                for="billing_city">
                                <p>City *</p>
                            </label>
                            <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.billing_city">{{ errors.billing_city }}</p></div>
                        </div>
                        <div class="grid-2 hide-billing">
                            <div class="selectore-state select-wrapper">
                                <CustomSelect
                                    :aria-required="selectedPaymentMethod == 'card' && !useShippingAsBilling"
                                    name="billing_state" id="billing_state" label="State" v-model="selectedBillingState" :options="props.regions"
                                    :class="{'invalid-state-input': errors.billing_state != null}"
                                />
                                <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.billing_state">{{ errors.billing_state }}</p></div>
                            </div>
                            <div class="authorization-input_label">
                                <InputText 
                                    :aria-required="selectedPaymentMethod == 'card' && !useShippingAsBilling"
                                    @focus="togglePlaceholder('billing_zipcode')"
                                    @blur="togglePlaceholder('billing_zipcode')"
                                    id="billing_zipcode" v-model="billingZip"
                                    :class="{
                                        'authorization-name-on-card_input': true,
                                        'invalid-state-input': errors.billing_zip != null,
                                    }"
                                    :placeholder="!placeholdersHidden.get('billing_zipcode') ? 'Zipcode *' : ''" 
                                    fluid
                                />
                                <label
                                    :class="{'invalid-state': errors.billing_zip}"
                                    for="billing_zipcode">
                                    <p>Zipcode *</p>
                                </label>
                                <div class="input-folder"><p class="input-support-text invalid-state" v-if="errors.billing_zip">{{ errors.billing_zip }}</p></div>
                            </div>
                        </div>
                        <div class="checkbox-container" v-if="isLoggedIn">
                            <label class="container">
                                <input type="checkbox" v-model="saveCard" id="save_card"/>
                                <p class="paragraph-details">Save this card for future use</p>
                            </label>
                        </div>
                        <div class="checkbox-container" v-if="isLoggedIn">
                            <label class="container">
                                <input type="checkbox" v-model="setAsDefault" id="set_as_default"/>
                                <p class="paragraph-details">Set this card as default</p>
                            </label>
                        </div>
                        <p class="paragraph-details block hide-billing" v-if="!isLoggedIn">Rothco currently only ships within the United States. If you live outside of the US please search for a dealer in your country <a :href="route('retail.locator.show')" target="_blank" class="paragraph-details">here.</a></p>
                    </div>
                </div>
            </label>
        </div>									
    </div>
    <div class="guest-checkout-container payment" v-show="!isOpen">
        <div class="flex flex-row" style="justify-content: space-between;">
            <p class="title-l text-rothcoBlack">Payment</p>
            <div v-if="props.user?.email" class="bg-rothcoCream accordion-button" @click="isOpen = true">
                <AccordionArrow />
            </div>
        </div>
        <p v-if="selectedPaymentMethod == 'card'">{{ cardNumber }}</p>
        <p v-else-if="selectedPaymentMethod == 'paypal'" class="image-container" style="display: flex; gap: 0 0.5rem;"><PaypalIcon alt="paypal-logo"/>PayPal</p>
        <p v-else-if="selectedPaymentMethod == 'stripe_express_checkout' && isStripeExpressCheckoutAvailable" class="image-container" style="display: flex; gap: 0 0.5rem;">
            <svg class="stripe-express-checkout-icon" viewBox="0 0 24 24" fill="none" style="width: 20px; height: 20px;">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" fill="currentColor"/>
            </svg>
            Express Checkout
        </p>
        <p v-else style="display: flex; gap: 0 0.5rem;">
            <MasterCardIcon v-if="selectedCreditCardRecord?.cc_type == 'Master Card' || selectedCreditCardRecord?.cc_type == 'MasterCard'" alt="mastercard-logo"/>
            <VisaIcon v-if="selectedCreditCardRecord?.cc_type == 'Visa'"  alt="visa-logo"/>
            <AmexIcon v-if="selectedCreditCardRecord?.cc_type == 'American Express'" alt="amex-logo"/>
            <DiscoverIcon v-if="selectedCreditCardRecord?.cc_type == 'Discover'" alt="discover-logo"/>
            {{ selectedCreditCardRecord?.cc_type }} &bull;&bull;&bull;&bull;
            {{ selectedCreditCardRecord?.cc_last_4 }}
        </p>
    </div>
    </section>
</template>
<script lang="ts" setup>
    import { computed, watch, ref } from "vue";
    import { route } from "ziggy-js";
    import InputMask from "primevue/inputmask";
    import InputText from 'primevue/inputtext';
    import AccordionArrow from "@icons/accordion-arrow.svg";
    import VisaIcon from "@icons/visa.svg";
    import MasterCardIcon from "@icons/mastercard.svg";
    import AmexIcon from "@icons/amex.svg";
    import PaypalIcon from "@icons/paypal.svg";
    import DiscoverIcon from "@icons/discover.svg";
    import WarningIcon from "@icons/notiftcation-warning-icon.svg";
    import LockIcon from "@icons/lock-red.svg";
    import StripeExpressCheckoutButton from './StripeExpressCheckoutButton.vue';
    let selectedPaymentMethod = defineModel("selectedPaymentMethod", {
        type: String,
        default: "card"
    });
    let cardNumber = defineModel("cardNumber", {
        type: String,
        default: ""
    });
    let expirationDate = defineModel("expirationDate", {
        type: String,
        default: ""
    });
    let securityCode = defineModel("securityCode", {
        type: String,
        default: ""
    });
    let nameOnCard = defineModel("nameOnCard", {
        type: String,
        default: ""
    });
    let useShippingAsBilling = defineModel("useShippingAsBilling", {
        type: Boolean,
        default: true
    });
    let billingFirstName = defineModel("billingFirstName", {
        type: String,
        default: ""
    });
    let billingLastName = defineModel("billingLastName", {
        type: String,
        default: ""
    });
    let billingAddress = defineModel("billingAddress", {
        type: String,
        default: ""
    });
    let billingAddress2 = defineModel("billingAddress2", {
        type: String,
        default: ""
    });
    let billingCity = defineModel("billingCity", {
        type: String,
        default: ""
    });
    const selectedBillingState = computed({
        get(){
            return props.regions?.find((region)=>(region.value == billingState.value)) ?? {};
        },
        set(newVal){
            billingState.value = newVal?.value;
        }
    })
    let billingState = defineModel("billingState", {
        type: String,
        default: {}
    });
    let billingZip = defineModel("billingZip", {
        type: String,
        default: ""
    });
    
    let saveCard = defineModel("saveCard", {
        type: Boolean,
        default: false
    });

    let setAsDefault = defineModel("setAsDefault", {
        type: Boolean,
        default: false
    });

    let errors = defineModel('errors', {
        type: Object,
        default(){
            return {};
        }
    });

    let addSecondAddress = ref(false);

    let props = defineProps({
        user: {
            type: Object,
            default() {
                return {};
            }
        },
        creditCards: {
            type: Array<Object>,
            default: []
        },
        regions: {
            type: Array<Object>,
            default: [],
        },
        paypalAttempted: {
            type: Boolean,
            default: false
        },
        stripePublishableKey: {
            type: String,
            required: true
        },
        totalAmount: {
            type: Number,
            required: true
        },
        canProcessStripeExpressCheckout: {
            type: Boolean,
            default: false
        }
    });

    let isOpen = ref(!props.user?.email || selectedPaymentMethod.value == 'card');
    let openNewCard = ref(false);
    let isLoggedIn = computed(() => {
        return props.user && props.user.email;
    });

    let emits = defineEmits(['creditCardUpdated', 'stripe-express-checkout-success'])

    const stripeExpressCheckoutButtonRef = ref(null);
    const isStripeExpressCheckoutAvailable = ref(true);

    let selectedCreditCardRecord = computed(() => {
        if (!["paypal", "card", "stripe_express_checkout"].includes(selectedPaymentMethod.value)) {
            return props.creditCards.find(
                (card) => card.consumer_credit_card_id == selectedPaymentMethod.value
            );
        }
        return null;
    });

    const creditCardsSorted = computed(()=>{
        let creditCards = JSON.parse(JSON.stringify(props.creditCards));
        creditCards = creditCards.sort((a, b) => {
            const aIsExpired = isCardExpired(a.cc_exp);
            const bIsExpired = isCardExpired(b.cc_exp);
            if (a.is_default == '1' && b.is_default != '1') return -1;
            if (a.is_default != '1' && b.is_default == '1') return 1;
            if (!aIsExpired && bIsExpired) return -1;
            if (aIsExpired && !bIsExpired) return 1;
            return 0;
        })
        return creditCards;
    })

    const placeholdersHidden = ref(new Map());
    function togglePlaceholder(id) {
        if(!placeholdersHidden.value.has(id)) {
            placeholdersHidden.value.set(id, true);
        }else{
            placeholdersHidden.value.set(id, !placeholdersHidden.value.get(id));
        }
    }

    function setCardPaymentMethod(){
        selectedPaymentMethod.value = 'card'; 
        openNewCard.value = true
    }

    const defaultCreditCard = computed(() => {
        return creditCardsSorted.value?.find(card=>{
            return !isCardExpired(card.cc_exp)
        }) ?? null;
    });

    const isCardExpired = (expiry) => {
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear() % 100;
        const cardMonth = parseInt(expiry.substring(0, 2), 10);
        const cardYear = parseInt(expiry.substring(2, 4), 10);
        return cardYear < currentYear || (cardYear === currentYear && cardMonth < currentMonth);
    };

    watch(()=>props.user?.user_id, (newVal)=>{
        if(newVal){
            selectedPaymentMethod.value = defaultCreditCard.value?.consumer_credit_card_id ?? 'card';
        }
    }, {immediate: true})

    watch(selectedPaymentMethod, (newVal)=>{
        if(newVal != 'card'){
            useShippingAsBilling.value = true;
        }
    })

    let creditCardInfoEntered = computed(()=>{
        return cardNumber.value && expirationDate.value && securityCode.value && nameOnCard.value;
    });

    watch(()=>creditCardInfoEntered, (newVal)=>{
        if(newVal){
            emits('creditCardUpdated');
        }
    })

    const handleStripeExpressCheckoutSuccess = (paymentData) => {
        selectedPaymentMethod.value = 'stripe_express_checkout';
        emits('stripe-express-checkout-success', paymentData);
    };

    const getStripeExpressCheckoutFormData = () => {
        if (selectedPaymentMethod.value === 'stripe_express_checkout' && stripeExpressCheckoutButtonRef.value) {
            return stripeExpressCheckoutButtonRef.value.form.data();
        }
        return null;
    };

    defineExpose({
        getStripeExpressCheckoutFormData
    });
   
</script>
<style lang="scss" scoped>
.icon-open {
    transform: scaleY(-1);
    -moz-transform: scaleY(-1);
    -webkit-transform: scaleY(-1);
    -ms-transform: scaleY(-1);
}
.accordion-button {
    padding: 0.375rem 0.8rem; 
    height: 50%; 
    cursor: pointer; 
    border-radius: 0.25rem;
}
</style>
